import React, { useState, useEffect } from 'react';
import { Users, Download, Eye, Filter, Calendar, Mail, Phone, FileText, MessageCircle, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import type { StatusCandidato } from '../types/rh';
import { formatToSaoPaulo } from '../utils/timezone';
import { CandidatoCadastroManual } from './CandidatoCadastroManual';
import { CandidatoDetalhesModal } from './CandidatoDetalhesModal';

interface CandidatoSupabase {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  curriculo_url: string | null;
  vaga_id: string | null;
  origem_lead: string | null;
  data_envio: string;
  status: StatusCandidato;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
  tipo_cadastro: string;
  vaga: { id: string; titulo: string; } | null;
}

const CandidatosRH: React.FC = () => {
  const [candidatos, setCandidatos] = useState<CandidatoSupabase[]>([]);
  const [loading, setLoading] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState<string>('todos');
  const [filtroOrigem, setFiltroOrigem] = useState<string>('todos');
  const [mostrarCadastroManual, setMostrarCadastroManual] = useState(false);
  const [candidatoDetalhesId, setCandidatoDetalhesId] = useState<string | null>(null);

  useEffect(() => {
    carregarCandidatos();
  }, []);

  const carregarCandidatos = async () => {
    try {
      const { data, error } = await supabase
        .from('candidatos_rh')
        .select(`
          *,
          vaga:vagas_rh(id, titulo)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCandidatos(data || []);
    } catch (error) {
      console.error('Erro ao carregar candidatos:', error);
    } finally {
      setLoading(false);
    }
  };

  const atualizarStatus = async (candidatoId: string, novoStatus: string) => {
    try {
      const { error } = await supabase
        .from('candidatos_rh')
        .update({ status: novoStatus as StatusCandidato })
        .eq('id', candidatoId);

      if (error) throw error;
      
      setCandidatos(prev => 
        prev.map(c => 
          c.id === candidatoId ? { ...c, status: novoStatus as StatusCandidato } : c
        )
      );
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const baixarCurriculo = async (curriculoUrl: string, nomeCandidato: string) => {
    try {
      console.log('🔍 Tentando baixar currículo:', curriculoUrl);
      
      // Criar um link de download direto
      const link = document.createElement('a');
      link.href = curriculoUrl;
      link.download = `curriculo-${nomeCandidato.replace(/\s+/g, '-')}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Adicionar ao DOM temporariamente e clicar
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Download iniciado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao baixar currículo:', error);
      
      // Fallback: tentar abrir em nova aba
      try {
        window.open(curriculoUrl, '_blank');
      } catch (fallbackError) {
        console.error('❌ Erro no fallback:', fallbackError);
        alert('Não foi possível baixar o arquivo. Tente novamente ou contate o suporte.');
      }
    }
  };

  const excluirCandidato = async (candidatoId: string, nomeCandidato: string) => {
    try {
      const opcao = window.prompt(
        `⚠️ EXCLUSÃO PERMANENTE DO CANDIDATO ⚠️\n\n` +
        `Candidato: ${nomeCandidato}\n\n` +
        'Esta ação irá excluir PERMANENTEMENTE:\n' +
        '• Todos os dados do candidato\n' +
        '• Histórico completo (timeline)\n' +
        '• Conversas com IA\n' +
        '• Entrevistas agendadas\n' +
        '• Documentos anexados\n\n' +
        'Esta ação NÃO PODE ser desfeita!\n\n' +
        'Digite "EXCLUIR" para confirmar ou qualquer outra coisa para cancelar:'
      );

      if (opcao !== 'EXCLUIR') {
        alert('Operação cancelada.');
        return;
      }

      // Confirmar novamente
      const confirmacaoFinal = confirm(
        `ÚLTIMA CONFIRMAÇÃO!\n\n` +
        `Tem ABSOLUTA CERTEZA que deseja excluir permanentemente o candidato "${nomeCandidato}"?\n\n` +
        `Esta é sua última chance de cancelar!`
      );

      if (!confirmacaoFinal) {
        alert('Operação cancelada.');
        return;
      }

      // Iniciar exclusão em cascata
      console.log('Iniciando exclusão do candidato:', candidatoId);

      // 1. Excluir timeline do candidato
      const { error: timelineError } = await supabase
        .from('timeline_candidatos')
        .delete()
        .eq('candidato_id', candidatoId);

      if (timelineError) throw timelineError;

      // 2. Excluir conversas IA
      const { error: conversasError } = await supabase
        .from('conversas_ia_rh')
        .delete()
        .eq('candidato_id', candidatoId);

      if (conversasError) throw conversasError;

      // 3. Excluir entrevistas
      const { error: entrevistasError } = await supabase
        .from('entrevistas_rh')
        .delete()
        .eq('candidato_id', candidatoId);

      if (entrevistasError) throw entrevistasError;

      // 4. Excluir documentos
      const { error: documentosError } = await supabase
        .from('documentos_candidatos')
        .delete()
        .eq('candidato_id', candidatoId);

      if (documentosError) throw documentosError;

      // 5. Finalmente, excluir o candidato
      const { error: candidatoError } = await supabase
        .from('candidatos_rh')
        .delete()
        .eq('id', candidatoId);

      if (candidatoError) throw candidatoError;

      alert(`Candidato "${nomeCandidato}" foi excluído permanentemente com sucesso!`);
      carregarCandidatos(); // Recarregar a lista
    } catch (error) {
      console.error('Erro ao excluir candidato:', error);
      alert('Erro ao excluir candidato: ' + (error as any)?.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'novo': return 'bg-blue-100 text-blue-800';
      case 'triagem': return 'bg-yellow-100 text-yellow-800';
      case 'entrevista_marcada': return 'bg-purple-100 text-purple-800';
      case 'reprovado': return 'bg-red-100 text-red-800';
      case 'contratado': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrigemIcon = (origem: string) => {
    return origem === 'lead' ? <MessageCircle className="h-4 w-4" /> : <FileText className="h-4 w-4" />;
  };

  const getOrigemText = (origem: string) => {
    switch (origem) {
      case 'lead': return 'Chat IA';
      case 'candidato': return 'Formulário';
      default: return origem;
    }
  };

  const getOrigemColor = (origem: string) => {
    switch (origem) {
      case 'lead': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'candidato': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const candidatosFiltrados = candidatos.filter(candidato => {
    const statusMatch = abaAtiva === 'todos' || candidato.status === abaAtiva;
    const origemMatch = filtroOrigem === 'todos' || candidato.tipo_cadastro === filtroOrigem;
    return statusMatch && origemMatch;
  });

  const statusOptions = [
    { value: 'todos', label: 'Todos', count: candidatos.length },
    { value: 'novo', label: 'Novos', count: candidatos.filter(c => c.status === 'novo').length },
    { value: 'triagem', label: 'Em Triagem', count: candidatos.filter(c => c.status === 'triagem').length },
    { value: 'entrevista_marcada', label: 'Entrevistas', count: candidatos.filter(c => c.status === 'entrevista_marcada').length },
    { value: 'reprovado', label: 'Reprovados', count: candidatos.filter(c => c.status === 'reprovado').length },
    { value: 'contratado', label: 'Contratados', count: candidatos.filter(c => c.status === 'contratado').length }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-8 h-8 text-orange-500 mr-3" />
              Candidatos
            </h1>
            <p className="text-gray-600">Gerencie candidatos e currículos</p>
          </div>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando candidatos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 text-orange-500 mr-3" />
            Candidatos
          </h1>
          <p className="text-gray-600">Gerencie candidatos e currículos</p>
        </div>
        <button
          onClick={() => setMostrarCadastroManual(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Cadastrar Candidato
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{candidatos.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Filter className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Em Triagem</p>
              <p className="text-2xl font-bold text-gray-900">
                {candidatos.filter(c => c.status === 'triagem').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Entrevistas</p>
              <p className="text-2xl font-bold text-gray-900">
                {candidatos.filter(c => c.status === 'entrevista_marcada').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Contratados</p>
              <p className="text-2xl font-bold text-gray-900">
                {candidatos.filter(c => c.status === 'contratado').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Abas por Status */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setAbaAtiva(option.value)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  abaAtiva === option.value
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {option.label}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  abaAtiva === option.value
                    ? 'bg-orange-100 text-orange-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {option.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Filtro de Origem */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">
              Filtrar por origem:
            </label>
            <select
              value={filtroOrigem}
              onChange={(e) => setFiltroOrigem(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="todos">Todas as origens</option>
              <option value="candidato">Formulário</option>
              <option value="lead">Chat IA</option>
            </select>
          </div>
        </div>

        {/* Lista de Candidatos */}
        <div className="overflow-hidden">
        {candidatosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Nenhum candidato encontrado
            </h3>
            <p className="text-gray-600">
              {candidatos.length === 0 
                ? 'Ainda não há candidatos cadastrados'
                : 'Nenhum candidato corresponde aos filtros selecionados'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vaga
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Origem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidatosFiltrados.map((candidato) => (
                  <tr key={candidato.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {candidato.nome}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <Mail className="h-3 w-3 mr-1" />
                          {candidato.email}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {candidato.telefone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {candidato.vaga?.titulo || 'Vaga não informada'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={candidato.status}
                        onChange={(e) => atualizarStatus(candidato.id, e.target.value)}
                        className={`px-2 py-1 text-xs font-medium rounded-full border-0 ${getStatusColor(candidato.status)}`}
                      >
                        <option value="novo">Novo</option>
                        <option value="triagem">Em triagem</option>
                        <option value="entrevista_marcada">Entrevista marcada</option>
                        <option value="reprovado">Reprovado</option>
                        <option value="contratado">Contratado</option>
                      </select>
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getOrigemColor(candidato.tipo_cadastro)}`}>
                         {getOrigemIcon(candidato.tipo_cadastro)}
                         <span className="ml-1">
                           {getOrigemText(candidato.tipo_cadastro)}
                         </span>
                       </span>
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatToSaoPaulo(candidato.created_at, 'dd/MM/yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {candidato.curriculo_url && (
                          <button
                            onClick={() => baixarCurriculo(candidato.curriculo_url!, candidato.nome)}
                            className="text-orange-600 hover:text-orange-900 transition-colors"
                            title="Baixar currículo"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setCandidatoDetalhesId(candidato.id)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => excluirCandidato(candidato.id, candidato.nome)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Excluir candidato permanentemente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {/* Modais */}
      <CandidatoCadastroManual
        isOpen={mostrarCadastroManual}
        onClose={() => setMostrarCadastroManual(false)}
        onCandidatoCriado={carregarCandidatos}
      />

      <CandidatoDetalhesModal
        candidatoId={candidatoDetalhesId}
        isOpen={candidatoDetalhesId !== null}
        onClose={() => setCandidatoDetalhesId(null)}
      />
    </div>
  );
};

export default CandidatosRH;